import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { Input } from '../components/Input';
import { OTPInput } from '../components/OTPInput';
import { PhoneInput } from '../components/PhoneInput';
import { AlertCircle, CheckCircle2, Loader2, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { loadTurnstileScript, refreshAuthProfile, resolveApiBaseUrl, syncAuthToExtension } from '../lib/utils';

type AuthVariant = 'page' | 'modal'

interface SignupScreenProps {
  variant?: AuthVariant
  onClose?: () => void
  onSwitch?: () => void
}

export const SignupScreen: React.FC<SignupScreenProps> = ({ variant = 'page', onClose, onSwitch }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpSuccess, setOtpSuccess] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [debugCode, setDebugCode] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const [turnstileState, setTurnstileState] = useState<{ status: 'loading' | 'enabled' | 'disabled'; siteKey: string }>(
    { status: 'loading', siteKey: '' }
  );
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  const turnstileRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetId = useRef<string | null>(null);

  const persistAuth = (payload: { token?: string } | null) => {
    if (!payload?.token) {
      return false;
    }
    try {
      localStorage.setItem('token', payload.token);
      localStorage.setItem('auth', JSON.stringify(payload ?? {}));
      localStorage.removeItem('guest_token');
      localStorage.removeItem('guest_user');
    } catch (error) {
      // ignore storage errors
    }
    return true;
  };

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = window.setTimeout(() => setCountdown((prev) => prev - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    let active = true;
    const fetchTurnstileConfig = async () => {
      try {
        const apiBaseUrl = resolveApiBaseUrl();
        const response = await fetch(`${apiBaseUrl}/api/v1/auth/turnstile`);
        const payload = await response.json().catch(() => null);
        if (!active) return;
        if (response.ok && payload?.enabled && payload?.site_key) {
          setTurnstileState({ status: 'enabled', siteKey: payload.site_key });
          setTurnstileError(null);
        } else {
          setTurnstileState({ status: 'disabled', siteKey: '' });
          setTurnstileError(t('captchaUnavailable'));
        }
      } catch (error) {
        if (active) {
          setTurnstileState({ status: 'disabled', siteKey: '' });
          setTurnstileError(t('captchaUnavailable'));
        }
      }
    };
    fetchTurnstileConfig();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (turnstileState.status !== 'enabled' || !turnstileState.siteKey || !turnstileRef.current) {
      return;
    }
    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !turnstileRef.current || !window.turnstile) {
          return;
        }
        if (turnstileWidgetId.current && window.turnstile.remove) {
          window.turnstile.remove(turnstileWidgetId.current);
          turnstileWidgetId.current = null;
        }
        turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
          sitekey: turnstileState.siteKey,
          callback: (token: string) => {
            setTurnstileToken(token);
            setTurnstileError(null);
          },
          'expired-callback': () => {
            setTurnstileToken(null);
          },
          'error-callback': () => {
            setTurnstileToken(null);
            setTurnstileError(t('captchaError'));
          }
        });
      })
      .catch(() => {
        if (!cancelled) {
          setTurnstileError(t('captchaLoadFailed'));
        }
      });
    return () => {
      cancelled = true;
      if (turnstileWidgetId.current && window.turnstile?.remove) {
        window.turnstile.remove(turnstileWidgetId.current);
        turnstileWidgetId.current = null;
      }
    };
  }, [turnstileState.status, turnstileState.siteKey, t]);

  const resetTurnstile = () => {
    if (turnstileWidgetId.current && window.turnstile?.reset) {
      window.turnstile.reset(turnstileWidgetId.current);
    }
    setTurnstileToken(null);
  };

  const handleSendCode = async () => {
    if (loading) return;
    const trimmedPhone = phone.replace(/[^\d]+/g, '');
    if (!trimmedPhone) {
      setSendError(t('phoneRequired'));
      return;
    }
    if (trimmedPhone.length !== 11) {
      setSendError(t('phoneInvalid'));
      return;
    }
    if (turnstileState.status === 'loading') {
      setTurnstileError(t('captchaLoading'));
      return;
    }
    if (turnstileState.status !== 'enabled') {
      setTurnstileError(t('captchaUnavailable'));
      return;
    }
    if (!turnstileToken) {
      setTurnstileError(t('captchaRequired'));
      return;
    }

    setLoading(true);
    setSendError(null);
    setDebugCode(null);
    try {
      const apiBaseUrl = resolveApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/api/v1/auth/sms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: trimmedPhone,
          turnstile_token: turnstileState.status === 'enabled' ? turnstileToken : undefined
        })
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        const retryAfter = Number(response.headers.get('Retry-After') || 0);
        if (response.status === 429 && retryAfter > 0) {
          setCountdown(retryAfter);
        }
        setSendError(payload?.detail || payload?.message || t('sendCodeFailed'));
        setLoading(false);
        resetTurnstile();
        return;
      }

      setCodeSent(true);
      setCountdown(60);
      setOtpValue('');
      setOtpError(null);
      setOtpSuccess(false);
      setTurnstileError(null);
      if (payload?.debug_code) {
        setDebugCode(String(payload.debug_code));
      }
      setLoading(false);
      resetTurnstile();
    } catch (error) {
      setSendError(t('sendCodeFailed'));
      setLoading(false);
      resetTurnstile();
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedPhone = phone.replace(/[^\d]+/g, '');
    if (!trimmedPhone) {
      setSendError(t('phoneRequired'));
      return;
    }
    if (trimmedPhone.length !== 11) {
      setSendError(t('phoneInvalid'));
      return;
    }
    if (!otpValue || otpValue.length !== 6) {
      setOtpError(t('invalidCode'));
      return;
    }
    if (!password) {
      setPasswordError(t('passwordRequired'));
      return;
    }
    if (password.length < 8) {
      setPasswordError(t('passwordTooShort'));
      return;
    }

    setLoading(true);
    setOtpError(null);
    setPasswordError(null);
    try {
      const apiBaseUrl = resolveApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/api/v1/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: trimmedPhone,
          code: otpValue,
          password,
          device: {
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
            platform: typeof navigator !== 'undefined' ? navigator.platform : ''
          }
        })
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        setOtpError(payload?.detail || t('invalidCode'));
        setLoading(false);
        return;
      }
      if (!persistAuth(payload)) {
        setOtpError(t('loginFailed'));
        setLoading(false);
        return;
      }
      const refreshed = await refreshAuthProfile();
      await syncAuthToExtension(payload?.token ?? refreshed?.token ?? '', refreshed?.user ?? payload?.user ?? null, apiBaseUrl);

      setOtpSuccess(true);
      setLoading(false);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('auth:updated'));
      }
      setTimeout(() => {
        if (onClose) {
          onClose();
          return;
        }
        navigate('/workspace');
      }, 900);
    } catch (error) {
      setOtpError(t('loginFailed'));
      setLoading(false);
    }
  };

  const isModal = variant === 'modal'
  const handleSwitch = () => {
    if (onSwitch) {
      onSwitch()
      return
    }
    navigate('/login')
  }

  const handleOtpChange = (code: string) => {
    setOtpValue(code);
    if (otpError) setOtpError(null);
    if (otpSuccess) setOtpSuccess(false);
  };

  const canSubmit = codeSent && otpValue.length === 6 && password.length >= 8 && !loading;

  return (
    <div
      data-auth-variant={variant}
      className={isModal ? 'w-full' : 'min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4'}
    >
    <div
      className={`relative w-full bg-white border border-slate-200 rounded-3xl shadow-xl shadow-slate-200/60 p-10 md:p-12 ${
        isModal ? '' : 'max-w-[440px]'
      }`}
    >
      {isModal && onClose && (
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-slate-400 hover:text-slate-600 transition-colors"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      )}
      <div className="text-center mb-10">
        <div className="flex justify-center mb-6">
          <Logo />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('createAccount')}</h1>
        <p className="text-slate-500 text-sm">{t('signupSubtitle')}</p>
      </div>

      <form className="space-y-6" onSubmit={handleSignup}>
        <div className="space-y-2">
          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-[0.08em]">
            {t('phoneLabel')}
          </label>
          <PhoneInput
            value={phone}
            placeholder={t('phonePlaceholder')}
            onChange={(event) => {
              setPhone(event.target.value);
              if (sendError) setSendError(null);
            }}
          />
        </div>

        <div className="space-y-2" data-turnstile-container="true">
          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-[0.08em]">
            {t('captchaLabel')}
          </label>
          <div className="relative rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 flex items-center justify-center min-h-[72px]">
            <div ref={turnstileRef} className="w-full flex justify-center" />
            {turnstileState.status === 'loading' && (
              <span className="absolute text-xs text-slate-400">{t('captchaLoading')}</span>
            )}
            {turnstileState.status === 'disabled' && (
              <span className="absolute text-xs text-slate-400">{t('captchaUnavailable')}</span>
            )}
          </div>
          <p className="text-xs text-slate-500">{t('captchaHint')}</p>
          {turnstileError && (
            <div className="flex items-center gap-1.5 text-error animate-in slide-in-from-top-1 duration-200">
              <AlertCircle size={14} />
              <span className="text-xs font-medium">{turnstileError}</span>
            </div>
          )}
        </div>

        {!codeSent && (
          <div className="pt-2">
            <button
              type="button"
              onClick={handleSendCode}
              disabled={loading}
              className="w-full flex items-center justify-center py-3.5 bg-primary/10 text-primary hover:bg-primary/20 rounded-xl font-semibold transition-all text-sm active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="animate-spin" size={20} /> : t('sendCode')}
            </button>
            {sendError && (
              <div className="mt-2 flex items-center gap-1.5 text-error animate-in slide-in-from-top-1 duration-200">
                <AlertCircle size={14} />
                <span className="text-xs font-medium">{sendError}</span>
              </div>
            )}
          </div>
        )}

        {codeSent && (
          <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-500">
            <div className="flex justify-between items-center">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-[0.08em]">
                {t('verifyLabel')}
              </label>
              <button
                type="button"
                onClick={handleSendCode}
                disabled={countdown > 0 || loading || otpSuccess}
                className={`text-[11px] font-bold uppercase tracking-wider transition-colors ${
                  countdown > 0 || loading || otpSuccess
                    ? 'text-slate-400 cursor-default'
                    : 'text-primary hover:text-blue-700 cursor-pointer'
                }`}
              >
                {countdown > 0 ? t('resendIn').replace('{time}', countdown.toString()) : t('resendNow')}
              </button>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <OTPInput
                    length={6}
                    onChange={handleOtpChange}
                    error={!!otpError}
                    success={otpSuccess}
                    readOnly={otpSuccess}
                  />
                </div>
                {otpSuccess && (
                  <div className="flex items-center justify-center text-success animate-in zoom-in duration-300">
                    <CheckCircle2 size={28} strokeWidth={2.5} className="fill-success/10" />
                  </div>
                )}
              </div>
              {otpError && (
                <div className="flex items-center gap-1.5 text-error animate-in slide-in-from-top-1 duration-200">
                  <AlertCircle size={14} />
                  <span className="text-xs font-medium">{otpError}</span>
                </div>
              )}
              {sendError && !otpError && (
                <div className="flex items-center gap-1.5 text-error animate-in slide-in-from-top-1 duration-200">
                  <AlertCircle size={14} />
                  <span className="text-xs font-medium">{sendError}</span>
                </div>
              )}
              {debugCode && (
                <div className="text-xs text-slate-500">
                  {t('debugCodeHint').replace('{code}', debugCode)}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Input
            label={t('passwordLabel')}
            type="password"
            placeholder={t('setPassPlaceholder')}
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              if (passwordError) setPasswordError(null);
            }}
            error={passwordError || ''}
          />
          {passwordError && (
            <div className="flex items-center gap-1.5 text-error animate-in slide-in-from-top-1 duration-200">
              <AlertCircle size={14} />
              <span className="text-xs font-medium">{passwordError}</span>
            </div>
          )}
        </div>

        <div className="flex items-start gap-3 py-1">
          <div className="flex items-center h-5">
            <input
              id="terms"
              type="checkbox"
              defaultChecked
              className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
            />
          </div>
          <label htmlFor="terms" className="text-xs text-slate-500 leading-relaxed">
            {t('agree')}{' '}
            <a href="#" className="text-primary hover:text-blue-700 font-semibold underline underline-offset-2">
              {t('userAgreement')}
            </a>{' '}
            {t('and')}{' '}
            <a href="#" className="text-primary hover:text-blue-700 font-semibold underline underline-offset-2">
              {t('privacyPolicy')}
            </a>
          </label>
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full bg-primary hover:bg-blue-700 text-white font-semibold py-4 rounded-xl transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 active:scale-[0.98] mt-2 flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {loading ? <Loader2 className="animate-spin" size={20} /> : t('getStarted')}
        </button>
      </form>

      <div className="text-center mt-8">
        <p className="text-sm text-slate-500">
          {t('haveAccount')}{' '}
          <button
            onClick={handleSwitch}
            className="text-primary font-bold hover:underline ml-1 hover:text-blue-700 transition-colors"
          >
            {t('login')}
          </button>
        </p>
      </div>
    </div>
    </div>
  );
};
